import * as React from 'react'

import type { CommentsPostCard_PostData } from '~publish/pages/Community/fragments/CommentsPostCard_Post'

import type { StyleWithCssVars } from './types'
import { getGridTemplateAreas, isMediaOnlyPost } from './utils'

const DEFAULT_MAX_TAGS = 3

/**
 * Groups all content-flag derivations and grid layout computation
 * for the PostMiniature card.
 *
 * Takes the post + condensed flag and returns everything the card
 * container and conditional rendering logic needs.
 */
type PostMiniatureLayout = {
  hasMultipleAssets: boolean
  hasOnlyMediaContent: boolean
  hasTags: boolean
  renderAssets: boolean
  renderBody: boolean
  tagsToRender: CommentsPostCard_PostData['tags']
  tagsRemainder: CommentsPostCard_PostData['tags']
  cardStyle: StyleWithCssVars
}

export function usePostMiniatureLayout(
  post: CommentsPostCard_PostData,
  condensed: boolean,
): PostMiniatureLayout {
  const { assets, tags } = post

  const hasAssets = assets.length > 0
  const hasTags = tags.length > 0
  const hasMultipleAssets = assets.length > 1
  const renderAssets = hasAssets && !!assets[0]
  const renderBody = hasTags || !condensed

  // When the body is rendered, the card is not "media only" from a layout
  // perspective — it will show either real text or a "no caption" placeholder.
  const hasOnlyMediaContent =
    isMediaOnlyPost(post.text) && hasAssets && !renderBody
  const hasStatusBadge =
    post.status === 'draft' || post.status === 'needs_approval'

  const lineClamp = hasTags ? 1 : 2

  const gridTemplateAreas = React.useMemo(
    () =>
      getGridTemplateAreas({
        condensed,
        renderBody,
        renderAssets,
        hasOnlyMediaContent,
        hasStatusBadge,
      }),
    [condensed, renderBody, renderAssets, hasOnlyMediaContent, hasStatusBadge],
  )

  const cardStyle: StyleWithCssVars = React.useMemo(
    () => ({
      '--post-miniature-text-line-clamp': lineClamp,
      '--post-miniature-grid-areas': gridTemplateAreas,
    }),
    [lineClamp, gridTemplateAreas],
  )

  const tagsToRender = tags.slice(0, DEFAULT_MAX_TAGS)
  const tagsRemainder = tags.slice(DEFAULT_MAX_TAGS)

  return {
    hasMultipleAssets,
    hasOnlyMediaContent,
    hasTags,
    renderAssets,
    renderBody,
    tagsToRender,
    tagsRemainder,
    cardStyle,
  }
}
